/**
 * Persistence boundary of the backend. Documents are JSON values addressed by (collection, id).
 * Implementations: MemoryStorage (tests, Node in-process mode, STORAGE=memory), LocalStorageAdapter (browser in-process
 * mode, ./storage-local.ts) and JsonFileStorage (self-hosted server, ../node/json-file-storage.ts). The contract test
 * suite every adapter must pass: tests/backend/storage-contract.ts (runStorageContract).
 *
 * Domain services (./services/*) see only this interface: they never touch files, Web Storage or a database, and
 * never keep references into the store (adapters store serialized copies; a value read or written is the caller's).
 *
 * What an adapter must guarantee
 *  - Per-document read-your-writes: once put/delete/batch resolved, every get/list sees the new value (or its
 *    absence); values round-trip as JSON (a value that is not serializable is refused before anything changes).
 *  - `batch(ops)` is atomic: all of its puts and deletes become visible and durable together, or none of them does,
 *    also across a crash of the process at any point (after a restart either the whole batch or nothing of it is
 *    there). Ops apply in order; several ops on one document: the last one wins. An invalid op (collection or id) is
 *    refused before anything changes. A batch of one document equals a plain put/delete.
 *  - Linearizable: each put / delete / batch takes effect at one moment between its call and its resolution (a caller
 *    that awaits its writes sees them in its order); the durable copy always ends equal to what readers see.
 *  - `list(collection)` returns every document of the collection (any order; callers sort).
 *  - Durability: a write is durable when its promise resolved (JsonFileStorage: fsync before resolving); `flush()`
 *    waits until every accepted write is durable, `close()` flushes. Adapters without durability (MemoryStorage) say so
 *    through `kind` / `volatile`.
 *  - Collections match COLLECTION_PATTERN, ids are 1..200 characters of any kind (adapters encode them as needed).
 *  Isolation between concurrent operations is NOT the adapter's job: services serialize read-modify-write cycles of a
 *  document with their KeyedMutex (per player, per zone, per lot); a batch writes what the caller computed under it.
 *
 * SQL adapters (SQLite / PostgreSQL; boundary only, no database dependency is part of this project yet)
 *  - Mapping: one table per collection, or one generic table `documents(collection TEXT, id TEXT, body JSON/JSONB,
 *    updated_at, PRIMARY KEY (collection, id))`; `body` is the document exactly as the services wrote it. Columns
 *    extracted for queries (e.g. accounts.username, replays.player_id + created_at) are derived from `body` and
 *    never the source of truth. `list` = `SELECT body WHERE collection = ?`.
 *  - put = UPSERT, delete = DELETE (reporting whether a row existed), batch = ONE transaction with every op in order
 *    (COMMIT = durable; a failed op rolls the whole transaction back and rejects the batch).
 *  - `flush()` resolves when every committed transaction is durable (synchronous commit), `close()` closes the pool.
 *  - The storage schema version and applied migrations live in the document `meta/schema` (./migrations.ts) like in
 *    every other adapter; SQL DDL changes of an adapter are the adapter's own concern (its own schema table).
 *  - Must pass runStorageContract (tests/backend/storage-contract.ts) plus a crash test of its own.
 */
import { BackendError } from '../../shared/protocol/errors';
import type { Logger } from './logger';

export interface StorageAdapter {
  readonly kind: string;
  /** true = nothing survives the process (MemoryStorage): a dev/test mode, never for real players. */
  readonly volatile?: boolean;
  /** Loads persisted data and recovers an interrupted batch (JsonFileStorage: its write-ahead journal); idempotent. */
  open(): Promise<void>;
  get<T>(collection: string, id: string): Promise<T | null>;
  put<T>(collection: string, id: string, value: T): Promise<void>;
  delete(collection: string, id: string): Promise<boolean>;
  list<T>(collection: string): Promise<T[]>;
  /** All-or-nothing write of several documents (see the header). */
  batch(ops: readonly StorageBatchOp[]): Promise<void>;
  /**
   * Optional: a consistent point-in-time copy of every document (the operator console's backups), taken in one
   * synchronous pass, so no batch is half in it. Adapters that keep every document in memory implement it.
   */
  snapshot?(): Promise<StorageSnapshotDocument[]>;
  /** Optional: the UTF-8 bytes and the number of the documents stored now (the backups' free-space check). */
  dataSize?(): { bytes: number; documents: number };
  /**
   * Optional (adapters with a durable store of their own): whether writes are refused until a restart and which
   * stored documents could not be read (/health, the console's server module).
   */
  health?(): StorageHealth;
  /**
   * Optional: an operator stores `value` (null: removes it) in place of a damaged document and clears its mark.
   * `dropQuarantine`: its copy in the quarantine goes too (the account it belongs to is deleted).
   */
  repairDamaged?(collection: string, id: string, value: unknown, opts?: { dropQuarantine?: boolean }): Promise<void>;
  /** Waits until all accepted writes are durable. */
  flush(): Promise<void>;
  close(): Promise<void>;
}

/** A stored document the adapter could not read (JsonFileStorage: a file that does not parse). */
export interface DamagedDocument {
  collection: string;
  id: string;
  /** Where it is stored (left in place: never overwritten until repaired). */
  file: string;
  /** Its copy in the quarantine (null: the copy failed). */
  quarantined: string | null;
}

/** StorageAdapter.health. */
export interface StorageHealth {
  /** Writes are refused until a restart (a committed batch could not be applied; the restart applies its journal). */
  failed: boolean;
  /** The error code (EIO, ENOSPC, ...) of that failure. */
  reason: string | null;
  /** Documents that are neither read nor overwritten (UNAVAILABLE 'document_damaged') until an operator repairs them. */
  damaged: DamagedDocument[];
}

/** One document of StorageAdapter.snapshot: its collection, id and JSON text. */
export interface StorageSnapshotDocument {
  collection: string;
  id: string;
  raw: string;
}

/** Copies every document of in-memory collections (collection -> id -> JSON text) in one synchronous pass. */
export function snapshotOf(data: ReadonlyMap<string, ReadonlyMap<string, string>>): StorageSnapshotDocument[] {
  const out: StorageSnapshotDocument[] = [];
  for (const [collection, docs] of data) for (const [id, raw] of docs) out.push({ collection, id, raw });
  return out;
}

/** One write of a batch: `put` stores `value` (a JSON value), `delete` removes the document if it exists. */
export type StorageBatchOp =
  | { op: 'put'; collection: string; id: string; value: unknown }
  | { op: 'delete'; collection: string; id: string; value?: undefined };

/** Shorthand constructors of batch ops. */
export const putOp = (collection: string, id: string, value: unknown): StorageBatchOp => ({ op: 'put', collection, id, value });
export const deleteOp = (collection: string, id: string): StorageBatchOp => ({ op: 'delete', collection, id });

export const COLLECTION_PATTERN = /^[a-z][a-z0-9_]{0,31}$/;

export function assertCollection(collection: string): void {
  if (!COLLECTION_PATTERN.test(collection)) throw new Error(`Invalid collection name "${collection}"`);
}

export function assertDocId(id: string): void {
  if (typeof id !== 'string' || id.length === 0 || id.length > 200) throw new Error('Invalid document id');
}

/** Damaged documents a logger was already told about (every list request would repeat it otherwise). */
const skippedLogged = new WeakMap<Logger, Set<string>>();

function noteSkipped(logger: Logger, fields: Record<string, unknown>): void {
  let seen = skippedLogged.get(logger);
  if (!seen) skippedLogged.set(logger, (seen = new Set()));
  const key = JSON.stringify(fields);
  if (seen.has(key) || seen.size >= 1000) return;
  seen.add(key);
  logger.warn('damaged document skipped: read as missing until an operator restores it (logged once)', fields);
}

/** Whether `err` is a storage's refusal of a damaged document (UNAVAILABLE 'document_damaged', see StorageHealth). */
export function isDocumentDamaged(err: unknown): boolean {
  return err instanceof BackendError && err.code === 'UNAVAILABLE' && (err.details as { reason?: unknown } | undefined)?.reason === 'document_damaged';
}

/**
 * `storage.get` for readers of OTHER players' documents (lists, ratings, names in a view everybody sees, background
 * sweeps): a damaged document reads as missing (null) and is logged, so that one damaged file fails only the requests
 * that need that very document (its owner's sign-in, profile, ...), not a list every player asks for. Readers that
 * need the document itself use `storage.get` (UNAVAILABLE 'document_damaged'). Other errors are thrown as they are.
 */
export async function getOrSkipDamaged<T>(storage: StorageAdapter, collection: string, id: string, logger?: Logger): Promise<T | null> {
  try {
    return await storage.get<T>(collection, id);
  } catch (err) {
    if (!isDocumentDamaged(err)) throw err;
    if (logger) noteSkipped(logger, { collection, id });
    return null;
  }
}

/**
 * The same for a reader that goes through a service's getter (auth.getAccount, profiles.getRecord): `read()` with a
 * damaged document reads as null (logged).
 */
export async function skipDamaged<T>(read: () => Promise<T | null>, logger?: Logger, what?: Record<string, unknown>): Promise<T | null> {
  try {
    return await read();
  } catch (err) {
    if (!isDocumentDamaged(err)) throw err;
    if (logger) noteSkipped(logger, { ...what, collection: ((err as BackendError).details as { collection?: unknown }).collection });
    return null;
  }
}

/** A batch op after validation: the document key parts and the serialized value (null = delete). */
export interface PreparedBatchWrite {
  collection: string;
  id: string;
  raw: string | null;
}

/**
 * Validates and serializes the ops of a batch (throws before anything is written) and folds several ops on one
 * document into the last one. The result keeps the order of each document's LAST op.
 */
export function prepareBatch(ops: readonly StorageBatchOp[]): PreparedBatchWrite[] {
  if (!Array.isArray(ops)) throw new Error('batch: ops must be an array');
  const byKey = new Map<string, PreparedBatchWrite>();
  for (const o of ops) {
    if (!o || (o.op !== 'put' && o.op !== 'delete')) throw new Error(`batch: unknown op "${String((o as { op?: unknown } | null)?.op)}"`);
    assertCollection(o.collection);
    assertDocId(o.id);
    let raw: string | null = null;
    if (o.op === 'put') {
      const s = JSON.stringify(o.value);
      if (s === undefined) throw new Error(`batch: the value of ${o.collection}/${o.id} is not a JSON value`);
      raw = s;
    }
    const key = `${o.collection}\u0000${o.id}`;
    byKey.delete(key);
    byKey.set(key, { collection: o.collection, id: o.id, raw });
  }
  return [...byKey.values()];
}

/** In-memory storage (documents kept as JSON strings for isolation). Volatile: nothing survives the process. */
export class MemoryStorage implements StorageAdapter {
  readonly kind: string = 'memory';
  readonly volatile = true;
  private readonly data = new Map<string, Map<string, string>>();

  async open(): Promise<void> {}

  async get<T>(collection: string, id: string): Promise<T | null> {
    const raw = this.data.get(collection)?.get(id);
    return raw === undefined ? null : (JSON.parse(raw) as T);
  }

  async put<T>(collection: string, id: string, value: T): Promise<void> {
    assertCollection(collection);
    assertDocId(id);
    const raw = JSON.stringify(value);
    if (raw === undefined) throw new Error(`put: the value of ${collection}/${id} is not a JSON value`);
    let c = this.data.get(collection);
    if (!c) this.data.set(collection, (c = new Map()));
    c.set(id, raw);
  }

  async delete(collection: string, id: string): Promise<boolean> {
    return this.data.get(collection)?.delete(id) ?? false;
  }

  async list<T>(collection: string): Promise<T[]> {
    const c = this.data.get(collection);
    return c ? [...c.values()].map((raw) => JSON.parse(raw) as T) : [];
  }

  /** Validates every op first, then applies them in one synchronous pass (nobody observes a half batch). */
  async batch(ops: readonly StorageBatchOp[]): Promise<void> {
    for (const w of prepareBatch(ops)) {
      if (w.raw === null) {
        this.data.get(w.collection)?.delete(w.id);
        continue;
      }
      let c = this.data.get(w.collection);
      if (!c) this.data.set(w.collection, (c = new Map()));
      c.set(w.id, w.raw);
    }
  }

  async snapshot(): Promise<StorageSnapshotDocument[]> {
    return snapshotOf(this.data);
  }

  async flush(): Promise<void> {}
  async close(): Promise<void> {}

  /** Debug/test helper: number of documents in a collection. */
  size(collection: string): number {
    return this.data.get(collection)?.size ?? 0;
  }
}
